import { useNavigate } from '@tanstack/react-router';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import {
  SealCheckIcon,
  WarningCircleIcon,
  EyeIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import useAuth from '@/api/auth/useAuth';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import type { WorkflowVersionSummary } from '@erp/rpc';
import { T, Var, useGT } from 'gt-react';

import {
  useVersionDraftDelete,
  useVersionPin,
} from './useVersionHistoryActions';
import { VersionChangelogEditor } from './VersionChangelogEditor';
import { StaleVersionPinDialog } from './StaleVersionPinDialog';
import { VersionStatusBadge } from './VersionStatusBadge';
import { WorkflowJsonDiffButton } from './WorkflowJsonDiffDialog';
export function WorkflowVersionHistoryRow({
  version,
  versions,
  stayInEditor = false,
  isViewing,
  isLastKnownGood,
  isOnlyVersion,
  onPinChanged,
  onView,
}: {
  version: WorkflowVersionSummary;
  versions: WorkflowVersionSummary[];
  stayInEditor?: boolean;
  isViewing: boolean;
  isLastKnownGood: boolean;
  /** Deleting the lineage's only version deletes the workflow with it. */
  isOnlyVersion: boolean;
  onPinChanged?: (newPinnedDefinitionId: string) => void;
  onView?: () => void;
}) {
  const { currentUser } = useAuth();
  const gt = useGT();
  const { pinMutation, pinStaleWarning, closeWarning, handlePin } =
    useVersionPin(version, onPinChanged);
  const {
    deleteDraftMutation,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    handleConfirmDelete,
  } = useVersionDraftDelete(version, { isViewing, onView });

  const isDraft = version.status === 'draft';
  const isOwnDraft = isDraft && version.createdBy === currentUser?.id;
  const busy = pinMutation.isPending || deleteDraftMutation.isPending;

  return (
    <div className="min-w-0 flex-1 py-2 pr-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-medium">
            <T context="workflow version number label, e.g. v3">
              v<Var>{version.version}</Var>
            </T>
          </span>
          <VersionStatusBadge
            status={version.status}
            isPinned={version.isPinned}
          />
          <VersionHistoryBadges
            version={version}
            isViewing={isViewing}
            isLastKnownGood={isLastKnownGood}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <WorkflowJsonDiffButton version={version} versions={versions} />
          <VersionHistoryViewButton
            version={version}
            isViewing={isViewing}
            stayInEditor={stayInEditor}
            onView={onView}
          />
          {!isDraft && !version.isPinned && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePin()}
              disabled={busy}
            >
              <SealCheckIcon className="mr-1 h-3 w-3" /> <T>Publish</T>
            </Button>
          )}
          {isOwnDraft && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={busy}
              title={gt('Delete this draft')}
              className="text-destructive hover:text-destructive"
            >
              <TrashIcon className="mr-1 h-3 w-3" /> <T>Delete</T>
            </Button>
          )}
        </div>
      </div>

      <VersionDraftDeleteDialog
        version={version}
        isOnlyVersion={isOnlyVersion}
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteDraftMutation.isPending}
      />

      {pinStaleWarning && (
        <StaleVersionPinDialog
          warning={pinStaleWarning}
          onClose={closeWarning}
          onConfirm={handlePin}
          loading={pinMutation.isPending}
        />
      )}

      <div className="mt-2 text-xs text-muted-foreground">
        {version.createdByName ?? version.createdBy} ·{' '}
        {new Date(version.createdAt).toLocaleString()}
      </div>

      <VersionChangelogEditor version={version} />
    </div>
  );
}

function VersionHistoryBadges({
  version,
  isViewing,
  isLastKnownGood,
}: {
  version: WorkflowVersionSummary;
  isViewing: boolean;
  isLastKnownGood: boolean;
}) {
  return (
    <>
      {isViewing && (
        <Badge variant="secondary">
          <T>Viewing</T>
        </Badge>
      )}
      {version.failingValidation && (
        <Badge variant="destructive">
          <WarningCircleIcon className="mr-1 h-3 w-3" weight="fill" />
          <T>Failed validation</T>
        </Badge>
      )}
      {isLastKnownGood && (
        <Badge variant="outline">
          <SealCheckIcon className="mr-1 h-3 w-3" />
          <T>Last known good</T>
        </Badge>
      )}
    </>
  );
}

function VersionDraftDeleteDialog({
  version,
  isOnlyVersion,
  open,
  onClose,
  onConfirm,
  loading,
}: {
  version: WorkflowVersionSummary;
  isOnlyVersion: boolean;
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  loading: boolean;
}) {
  const gt = useGT();
  return (
    <ConfirmDialog
      open={open}
      onClose={() => onClose()}
      onConfirm={onConfirm}
      title={
        isOnlyVersion
          ? gt('Delete this workflow?')
          : gt('Delete draft v{version}?', { version: version.version })
      }
      message={
        isOnlyVersion
          ? gt(
              'This draft is the only version of this workflow, so deleting it deletes the whole workflow — including its run history. This cannot be undone.',
            )
          : gt(
              'Any unfinalized changes in this draft will be discarded. Finalized versions are not affected.',
            )
      }
      confirmText={isOnlyVersion ? gt('Delete workflow') : gt('Delete draft')}
      dangerous
      loading={loading}
    />
  );
}

function VersionHistoryViewButton({
  version,
  isViewing,
  stayInEditor,
  onView,
}: {
  version: WorkflowVersionSummary;
  isViewing: boolean;
  stayInEditor: boolean;
  onView?: () => void;
}) {
  const navigate = useNavigate();
  const viewSearch = stayInEditor ? { edit: true } : undefined;
  const handleView = () => {
    onView?.();
    // Preserve edit mode for the editor panel; the versions tab opens a read view.
    if (version.isPinned) {
      void navigate({
        to: '/workflows/$workflowId',
        params: { workflowId: version.workflowId },
        search: viewSearch,
      });
    } else {
      void navigate({
        to: '/workflows/$workflowId/v/$versionNumber',
        params: {
          workflowId: version.workflowId,
          versionNumber: String(version.version),
        },
        search: viewSearch,
      });
    }
  };

  if (isViewing) return null;
  return (
    <Button size="sm" variant="ghost" onClick={handleView}>
      <EyeIcon className="mr-1 h-3 w-3" /> <T>View</T>
    </Button>
  );
}
